<script lang="ts" setup>
import {computed, reactive, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import useSwr from "@/core/composables/swr/useSwr";
import {loyaltyCardApi} from "@/container";
import Icon from "@/core/components/icon/Icon.vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import CustomerSearch from "@/customer/components/customer-search/CustomerSearch.vue";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import DateInput from "@/core/components/date-picker/DateInput.vue";
import {getLocalTimeZone, today} from "@internationalized/date";
import {LoyaltyCardBigBuyer} from "@/loyalty-card/interfaces/LoyaltyCardMigration";
import {LoyaltyCardProduct} from "@/loyalty-card/interfaces/LoyaltyCard";
import useLoyaltyCardAnalysis from "@/loyalty-card/composables/useLoyaltyCardAnalysis";
import LoyaltyCardService from "@/loyalty-card/services/LoyaltyCardService";
import dateHelper from "@/core/helpers/dateHelper";
import {useToastStore} from "@/stores/toast.store";
import {isAxiosError} from "axios";

/** Minimal shape read from the customer-search selection. */
type SearchedCustomer = {
    id: number;
    information?: { name?: string; birth_date?: string | null };
};

const props = defineProps<{
    loyaltyCardId: string;
}>();

const route = useRoute();
const router = useRouter();
const toast = useToastStore();

const resourceId = Number(props.loyaltyCardId);

/** Fallback threshold used while the card rule is still loading. */
const DEFAULT_THRESHOLD = 10;

const {data: loyaltyCard} = useLoyaltyCardAnalysis(resourceId);
const {resolveCardModel} = LoyaltyCardService();

/** The card reward model, resolved centrally. Defaults to product while loading. */
const cardModel = computed(() => (loyaltyCard.value ? resolveCardModel(loyaltyCard.value) : "product"));
/** Whether the migration preview uses the stamp model (product or mixed card). */
const isProductCard = computed(() => cardModel.value !== "point");
/** Whether the migration preview uses the points-gauge model. */
const isPointCard = computed(() => cardModel.value === "point");

/** Reward threshold: card objective (products) or point target (points). */
const threshold = computed(() => {
    const card = loyaltyCard.value;

    if (!card) {
        return DEFAULT_THRESHOLD;
    }

    if (isPointCard.value) {
        return card.point?.value ?? 0;
    }

    return card.objectives?.[0]?.value ?? DEFAULT_THRESHOLD;
});

/** Plural unit label used across the recap. */
const unitLabel = computed(() => (isPointCard.value ? "points" : "produits"));

/** Reward rule teaser, from the first objective teaser, falling back to the point teaser. */
const ruleTeaser = computed(() => {
    const card = loyaltyCard.value;

    return card?.objectives?.find(objective => objective.reward?.teaser)?.reward?.teaser
        ?? card?.point?.teaser
        ?? null;
});

const step = ref<1 | 2 | 3>(1);
const sortBy = ref<"count" | "date">("count");
const selectedBuyer = ref<LoyaltyCardBigBuyer | null>(null);
const checked = reactive<Record<string, boolean>>({});
const manual = ref<Array<{ id: string; product_id: number; name: string; quantity: number; date: string }>>([]);
/** Gives each manually added purchase a stable line id. */
let manualSequence = 0;
/** Lines the last creation attempt was refused for, as outside the card, by line id. */
const refused = reactive<Record<string, boolean>>({});
const showManual = ref(false);
const manualProduct = ref<{ id: number; name: string } | null>(null);
const manualQty = ref(1);
const manualDate = ref("");
/** Whether the typed manual purchase date is empty or a valid past date. */
const manualDateValid = ref(true);
/** A purchase date cannot be in the future. */
const todayDate = today(getLocalTimeZone());
const submitting = ref(false);

const {data: bigBuyersData, isLoading} = useSwr(
    computed(() => `loyalty-card/${resourceId}/big-buyers`),
    () => loyaltyCardApi().getBigBuyers(resourceId)
);

const buyers = computed(() => (bigBuyersData.value?.data ?? [])
    .slice()
    .sort((a, b) => (sortBy.value === "count"
        ? b.purchase_count - a.purchase_count
        : b.last_purchase_timestamp - a.last_purchase_timestamp)));

const purchases = computed(() => selectedBuyer.value?.purchases ?? []);

/** Stamps represented by the current selection (product model). */
const selectedCount = computed(() => {
    const fromPurchases = purchases.value
        .filter((purchase) => checked[purchase.id])
        .reduce((sum, purchase) => sum + purchase.quantity, 0);
    const fromManual = manual.value.reduce((sum, entry) => sum + Number(entry.quantity || 1), 0);

    return fromPurchases + fromManual;
});

/** Number of historical purchase lines to import (points model preview). */
const selectedLineCount = computed(() =>
    purchases.value.filter((purchase) => checked[purchase.id]).length + manual.value.length);

const cappedCount = computed(() => Math.min(selectedCount.value, threshold.value));
/** Stamps past the threshold: the card keeps them, shown in one extra cell after the last stamp. */
const overflow = computed(() => Math.max(0, selectedCount.value - threshold.value));
const remaining = computed(() => Math.max(0, threshold.value - selectedCount.value));
const stamps = computed(() => Array.from({length: threshold.value}, (_, index) => ({
    n: index + 1,
    filled: index < cappedCount.value
})));

/** Render individual stamps only for a reasonably sized product threshold. */
const showStamps = computed(() => isProductCard.value && threshold.value > 0 && threshold.value <= 20);
/** Progress percentage for the product bar (large thresholds). */
const productPercent = computed(() => (threshold.value
    ? Math.min(100, (selectedCount.value / threshold.value) * 100)
    : 0));

const clientName = computed(() => selectedBuyer.value?.name ?? "—");
const clientBirthDate = computed(() => selectedBuyer.value?.birth_date);

const allSelected = computed(() => purchases.value.length > 0 && purchases.value.every((purchase) => checked[purchase.id]));
const toggleAllLabel = computed(() => (allSelected.value ? "Tout désélectionner" : "Tout sélectionner"));

const initials = (name: string): string => name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const stepCircleClass = (index: number) => (step.value >= index
    ? "bg-primary text-white"
    : "bg-gray-light text-slate-500");
const stepLabelClass = (index: number) => (step.value >= index ? "text-primary font-semibold" : "text-slate-500");

const clearRefused = () => {
    Object.keys(refused).forEach((key) => delete refused[key]);
};

const selectBuyer = (buyer: LoyaltyCardBigBuyer) => {
    selectedBuyer.value = buyer;
    Object.keys(checked).forEach((key) => delete checked[key]);
    clearRefused();
    buyer.purchases.forEach((purchase) => {
        checked[purchase.id] = true;
    });
    manual.value = [];
    showManual.value = buyer.purchases.length === 0;
    step.value = 2;
};

/**
 * Select a patient from the free-text search: reuse the detected big-buyer
 * entry (with its purchases) when the patient is already a suggestion,
 * otherwise start from an empty detected list.
 */
const selectCustomer = (customer?: SearchedCustomer) => {
    if (!customer) {
        return;
    }

    const matched = (bigBuyersData.value?.data ?? []).find((buyer) => buyer.id === customer.id);

    if (matched) {
        selectBuyer(matched);

        return;
    }

    selectBuyer({
        id: customer.id,
        name: customer.information?.name ?? "Patient",
        birth_date: customer.information?.birth_date ?? null,
        purchase_count: 0,
        last_purchase: null,
        last_purchase_timestamp: 0,
        purchases: []
    });
};

const handlePatientSearch = (_type: string, _id: number, _key: string, customer?: SearchedCustomer) => {
    selectCustomer(customer);
};

/** Assign a patient in the "blank card" flow, keeping any manual purchases. */
const assignPatient = (customer?: SearchedCustomer) => {
    if (!customer) {
        return;
    }

    selectedBuyer.value = {
        id: customer.id,
        name: customer.information?.name ?? "Patient",
        birth_date: customer.information?.birth_date ?? null,
        purchase_count: 0,
        last_purchase: null,
        last_purchase_timestamp: 0,
        purchases: []
    };
};

const handlePatientAssign = (_type: string, _id: number, _key: string, customer?: SearchedCustomer) => {
    assignPatient(customer);
};

/** Start a blank card (no detected purchases); the patient is chosen in step 2. */
const blankCard = () => {
    selectedBuyer.value = null;
    Object.keys(checked).forEach((key) => delete checked[key]);
    clearRefused();
    manual.value = [];
    showManual.value = true;
    step.value = 2;
};

const toggleLine = (id: string) => {
    checked[id] = !checked[id];
};

const toggleAll = () => {
    const all = allSelected.value;
    purchases.value.forEach((purchase) => {
        checked[purchase.id] = !all;
    });
};

/** Search products restricted to the loyalty card's eligible range. */
const searchCardProducts = async (query: string): Promise<LoyaltyCardProduct[]> => {
    const response = await loyaltyCardApi().products(resourceId, {search: query, size: 20, type: 'eligible'});

    return response?.data ?? [];
};

const onManualProduct = (product: { id: number; name: string }) => {
    manualProduct.value = {id: product.id, name: product.name};
};

const addManual = () => {
    if (!manualProduct.value || !manualDateValid.value) {
        return;
    }

    const quantity = Number(manualQty.value) || 1;

    manual.value.push({
        id: `manual-${++manualSequence}`,
        product_id: manualProduct.value.id,
        name: manualProduct.value.name,
        quantity,
        date: manualDate.value || "date à préciser"
    });
    manualProduct.value = null;
    manualQty.value = 1;
    manualDate.value = "";
    showManual.value = false;
};

const removeManual = (index: number) => {
    manual.value.splice(index, 1);
};

const goBackToList = () => {
    router.push({name: "program.detail.consumers", params: {programId: route.params.programId}});
};

/**
 * The message shown when the card creation fails for another reason than
 * refused lines, with the server's reason when it gives one.
 */
const createCardError = (error: unknown): string => {
    if (isAxiosError(error) && error.response?.status === 422 && error.response.data?.message) {
        return error.response.data.message;
    }

    return "Une erreur est survenue lors de la création de la carte.";
};

/**
 * The lines the card would not credit, read from the validation errors the
 * server keys by purchase index (`purchases.<index>.product_id`).
 */
const refusedLineIds = (error: unknown, lineIds: string[]): string[] => {
    if (!isAxiosError(error) || error.response?.status !== 422) {
        return [];
    }

    return Object.keys(error.response.data?.errors ?? {})
        .map((key) => /^purchases\.(\d+)\.product_id$/.exec(key)?.[1])
        .map((index) => (index === undefined ? undefined : lineIds[Number(index)]))
        .filter((lineId): lineId is string => lineId !== undefined);
};

/** Tells the pharmacist how many lines to take out, and how. */
const refusedMessage = (lineIds: string[]): string => {
    const many = lineIds.length > 1;
    // A manual line has no checkbox: it can only be removed.
    const action = manual.value.some((entry) => lineIds.includes(entry.id)) ? "Retirez" : "Décochez";

    return `${lineIds.length} achat${many ? "s" : ""} ne ${many ? "font" : "fait"} pas partie des produits `
        + `ciblés par la carte. ${action}-${many ? "les" : "le"} pour créer la carte.`;
};

const createCard = async () => {
    if (submitting.value) {
        return;
    }

    const selectedPurchases = purchases.value.filter((purchase) => checked[purchase.id]);
    /** The line behind each payload entry, in the order the server indexes its errors. */
    const lineIds = [...selectedPurchases.map((purchase) => purchase.id), ...manual.value.map((entry) => entry.id)];
    const payload = [
        ...selectedPurchases.map((purchase) => ({
            product_id: purchase.product_id,
            quantity: purchase.quantity,
            date: purchase.date,
            purchased_at: purchase.purchased_at,
            price_value: purchase.price_value
        })),
        ...manual.value.map((entry) => ({
            product_id: entry.product_id,
            name: entry.name,
            quantity: entry.quantity,
            date: entry.date
        }))
    ];

    // A real customer is required to persist the card; the "blank card" path
    // still needs a patient picker (follow-up) before it can create.
    if (selectedBuyer.value?.id) {
        submitting.value = true;
        clearRefused();

        try {
            await loyaltyCardApi().createPrefilledCard(selectedBuyer.value.id, resourceId, payload);
        } catch (error) {
            const refusedLines = refusedLineIds(error, lineIds);

            if (refusedLines.length) {
                // Back to the purchases, where the refused lines are flagged.
                refusedLines.forEach((lineId) => {
                    refused[lineId] = true;
                });
                toast.showError({content: refusedMessage(refusedLines)});
                step.value = 2;
            } else {
                // Stay on the recap so the pharmacist can try again.
                toast.showError({content: createCardError(error)});
            }

            return;
        } finally {
            submitting.value = false;
        }
    }

    goBackToList();
};
</script>

<template>
  <BaseContainer>
    <div class="m-6 flex flex-col gap-5">
      <div
        class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary cursor-pointer w-fit"
        @click="goBackToList"
      >
        <Icon name="mdi-arrow-left" :size="18" color="#3513e2" />Retour aux cartes clients
      </div>

      <!-- header + stepper -->
      <div class="bg-white shadow rounded-lg p-5 flex items-center justify-between gap-6 flex-wrap">
        <div>
          <h2 class="text-lg font-bold text-primary">
            Migration papier → digital
          </h2>
          <p class="text-[13px] text-slate-500 mt-1">
            Apodis suggère vos gros acheteurs à partir de l'historique de vente.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <template v-for="s in [1, 2, 3]" :key="s">
            <div class="flex items-center gap-2">
              <span
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold"
                :class="stepCircleClass(s)"
              >{{ s }}</span>
              <span class="text-xs" :class="stepLabelClass(s)">
                {{ s === 1 ? 'Client' : s === 2 ? 'Achats' : 'Création' }}
              </span>
            </div>
            <div v-if="s < 3" class="w-6 h-0.5" :class="step > s ? 'bg-primary' : 'bg-gray'" />
          </template>
        </div>
      </div>

      <!-- STEP 1 : client selection -->
      <div v-if="step === 1" class="bg-white shadow rounded-lg overflow-hidden">
        <div class="p-5 border-b border-gray-light flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div class="font-semibold text-sm text-gray-dark">
              Sélectionnez un client
            </div>
            <div class="text-xs text-slate-500 mt-0.5">
              Recherchez un patient, ou choisissez un gros acheteur suggéré ci-dessous.
            </div>
          </div>
          <div class="min-w-[280px]">
            <CustomerSearch
              :callback="handlePatientSearch"
              placeholder="Rechercher un patient (nom, prénom)…"
              class="w-full"
            />
          </div>
        </div>

        <div class="px-5 py-3 border-b border-gray-light flex items-center gap-2">
          <span class="text-xs text-slate-500 mr-1">Trier par</span>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
            :class="sortBy === 'count' ? 'border-primary bg-primary-lightest text-primary' : 'border-slate-200 bg-white text-slate-500'"
            @click="sortBy = 'count'"
          >
            <Icon name="mdi-sort-numeric-variant" :size="15" :color="sortBy === 'count' ? '#3513e2' : '#64748b'" />Nombre d'achats
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
            :class="sortBy === 'date' ? 'border-primary bg-primary-lightest text-primary' : 'border-slate-200 bg-white text-slate-500'"
            @click="sortBy = 'date'"
          >
            <Icon name="mdi-sort-calendar-descending" :size="15" :color="sortBy === 'date' ? '#3513e2' : '#64748b'" />Dernier achat
          </button>
        </div>

        <div v-if="isLoading" class="py-10">
          <BaseFlexSpinner />
        </div>

        <div v-else-if="!buyers.length" class="px-5 py-10 text-center text-sm text-slate-500">
          Aucun gros acheteur détecté pour cette gamme.
        </div>

        <div
          v-for="buyer in buyers"
          :key="buyer.id"
          class="grid grid-cols-[2fr_1fr_1.2fr_auto] gap-4 items-center px-5 py-3 border-b border-gray-light"
        >
          <div class="flex items-center gap-3">
            <span class="w-9 h-9 rounded-full bg-primary-lightest text-primary flex items-center justify-center text-xs font-semibold shrink-0">
              {{ initials(buyer.name) }}
            </span>
            <div>
              <div class="text-[13px] font-semibold text-gray-dark">
                {{ buyer.name }}
              </div>
              <div class="text-[11px] text-slate-500">
                Né(e) le {{ buyer.birth_date ? dateHelper.shortFormat(buyer.birth_date) : '' }}
              </div>
            </div>
          </div>
          <div class="text-center">
            <span class="text-base font-semibold text-primary tabular-nums">{{ buyer.purchase_count }}</span>
            <div class="text-[11px] text-slate-500">
              achats
            </div>
          </div>
          <div class="text-xs text-slate-500">
            {{ buyer.last_purchase }}
          </div>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 bg-primary text-white rounded-lg px-3 py-2 text-xs font-semibold transition-opacity hover:opacity-90"
            @click="selectBuyer(buyer)"
          >
            <Icon name="mdi-card-plus-outline" :size="15" color="#fff" />Créer la carte digitale
          </button>
        </div>

        <div class="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
          <span class="text-xs text-slate-500">Le patient n'est pas dans la liste ?</span>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 border border-primary text-primary rounded-lg px-3 py-2 text-xs font-semibold hover:bg-primary-lightest transition-colors"
            @click="blankCard"
          >
            <Icon name="mdi-account-plus-outline" :size="15" color="#3513e2" />Créer une carte à blanc
          </button>
        </div>
      </div>

      <!-- STEP 2 : purchase selection -->
      <div v-else-if="step === 2" class="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-5 items-start">
        <div class="bg-white shadow rounded-lg overflow-hidden">
          <div class="flex items-center gap-3 p-5 border-b border-gray-light flex-wrap">
            <span class="w-11 h-11 rounded-full bg-primary-lightest text-primary flex items-center justify-center shrink-0">
              <Icon name="mdi-account" :size="24" color="#3513e2" />
            </span>
            <div class="flex-1 min-w-[160px]">
              <div class="text-base font-semibold" :class="selectedBuyer ? 'text-gray-dark' : 'text-slate-400'">
                {{ selectedBuyer ? clientName : 'Nouveau patient' }}
              </div>
              <div v-if="selectedBuyer" class="text-xs text-slate-500">
                Né(e) le {{ clientBirthDate ? dateHelper.shortFormat(clientBirthDate) : '' }}
              </div>
              <div v-else class="text-xs text-slate-500">
                Sélectionnez le patient pour créer sa carte.
              </div>
            </div>
            <div v-if="!selectedBuyer" class="min-w-[240px]">
              <CustomerSearch
                :callback="handlePatientAssign"
                placeholder="Rechercher le patient…"
                class="w-full"
              />
            </div>
            <span
              v-else-if="purchases.length"
              class="bg-secondary-lightest text-secondary-darkest text-[11px] font-bold px-3 py-1 rounded-full"
            >
              <Icon name="mdi-auto-fix" :size="13" color="#419e83" /> Achats détectés
            </span>
          </div>

          <div class="p-5">
            <div class="flex items-center justify-between mb-3">
              <div class="text-[13px] font-semibold text-gray-dark">
                Cochez les achats à ajouter à la carte
              </div>
              <span
                v-if="purchases.length"
                class="text-xs text-primary font-semibold cursor-pointer"
                @click="toggleAll"
              >{{ toggleAllLabel }}</span>
            </div>

            <div class="flex flex-col gap-2">
              <div
                v-for="purchase in purchases"
                :key="purchase.id"
                class="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer border"
                :class="checked[purchase.id] ? 'bg-primary-lightest border-primary' : 'bg-white border-slate-200'"
                @click="toggleLine(purchase.id)"
              >
                <span
                  class="w-5 h-5 rounded flex items-center justify-center shrink-0 border-2"
                  :class="checked[purchase.id] ? 'bg-primary border-primary' : 'bg-white border-gray'"
                >
                  <Icon v-if="checked[purchase.id]" name="mdi-check" :size="14" color="#fff" />
                </span>
                <div class="flex-1">
                  <div class="text-[13px] text-gray-dark">
                    {{ purchase.product }}
                  </div>
                  <div class="text-[11px] text-slate-500">
                    {{ purchase.date }}
                  </div>
                </div>
                <span v-if="refused[purchase.id]" class="text-[10px] font-bold text-red-light bg-red-light/15 px-2 py-0.5 rounded-full">Hors programme</span>
                <span class="text-[11px] text-slate-500">×{{ purchase.quantity }}</span>
                <span class="text-[13px] font-semibold text-gray-dark w-14 text-right">{{ purchase.price }}</span>
              </div>

              <div
                v-for="(entry, index) in manual"
                :key="entry.id"
                class="flex items-center gap-3 px-3 py-2.5 rounded-md bg-lightPurple-lightest border border-lightPurple"
              >
                <span class="w-5 h-5 rounded bg-primary flex items-center justify-center shrink-0">
                  <Icon name="mdi-check" :size="14" color="#fff" />
                </span>
                <div class="flex-1">
                  <div class="text-[13px] text-gray-dark">
                    {{ entry.name }}
                  </div>
                  <div class="text-[11px] text-slate-500">
                    {{ entry.date }}
                  </div>
                </div>
                <span v-if="refused[entry.id]" class="text-[10px] font-bold text-red-light bg-red-light/15 px-2 py-0.5 rounded-full">Hors programme</span>
                <span class="text-[11px] text-slate-500">×{{ entry.quantity }}</span>
                <span class="text-[10px] font-bold text-lightPurple-darkest bg-white px-2 py-0.5 rounded-full">Manuel</span>
                <button
                  type="button"
                  class="shrink-0 hover:opacity-70 transition-opacity"
                  title="Retirer"
                  @click="removeManual(index)"
                >
                  <Icon name="mdi-close" :size="16" color="#94a3b8" />
                </button>
              </div>

              <div v-if="!purchases.length && !manual.length" class="text-center py-4 text-xs text-slate-500">
                Aucun achat détecté automatiquement. Ajoutez les achats manuellement ci-dessous.
              </div>
            </div>

            <div class="mt-4 pt-4 border-t border-dashed border-gray">
              <div
                v-if="!showManual"
                class="inline-flex items-center gap-1.5 text-xs font-semibold text-primary cursor-pointer"
                @click="showManual = true"
              >
                <Icon name="mdi-plus-circle-outline" :size="16" color="#3513e2" />Vente introuvable ? Ajouter un achat manuellement
              </div>
              <div v-else class="bg-primary-lightest rounded-lg p-4">
                <div class="text-xs font-semibold text-gray-dark mb-3">
                  Ajouter un achat manuellement
                </div>
                <div class="flex gap-3 items-end flex-wrap">
                  <div class="flex-1 min-w-[200px]">
                    <div class="text-[11px] text-slate-500 mb-1">
                      Produit
                    </div>
                    <BaseProductSearchBar
                      placeholder="Rechercher un produit de la carte…"
                      :search-fn="searchCardProducts"
                      @select="onManualProduct"
                    />
                    <div v-if="manualProduct" class="text-[11px] text-primary font-semibold mt-1">
                      <Icon name="mdi-check" :size="12" color="#3513e2" />{{ manualProduct.name }}
                    </div>
                  </div>
                  <div class="w-20">
                    <div class="text-[11px] text-slate-500 mb-1">
                      Quantité
                    </div>
                    <input
                      v-model.number="manualQty"
                      type="number"
                      min="1"
                      class="w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-none tabular-nums"
                    >
                  </div>
                  <div class="w-36">
                    <div class="text-[11px] text-slate-500 mb-1">
                      Date d'achat
                    </div>
                    <DateInput
                      v-model="manualDate"
                      v-model:valid="manualDateValid"
                      :max-value="todayDate"
                    />
                  </div>
                  <button
                    type="button"
                    class="bg-primary text-white rounded-lg px-4 py-2 text-sm font-semibold transition-opacity"
                    :class="manualProduct && manualDateValid ? 'hover:opacity-90' : 'opacity-60 cursor-not-allowed'"
                    :disabled="!manualProduct || !manualDateValid"
                    @click="addManual"
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- recap -->
        <div class="bg-white shadow rounded-lg p-5 border border-lightPurple md:sticky md:top-4">
          <div class="text-xs text-slate-500 font-medium">
            {{ isPointCard ? 'Aperçu de la carte à points' : 'Aperçu de la carte digitale' }}
          </div>

          <!-- Product / stamp model -->
          <template v-if="!isPointCard">
            <div class="flex items-baseline gap-2 mt-1">
              <span class="text-3xl font-semibold text-primary tabular-nums">{{ selectedCount }}/{{ threshold }}</span>
              <span class="text-[13px] text-gray-dark">{{ unitLabel }}</span>
            </div>
            <div v-if="remaining > 0" class="text-xs text-emerald-600 font-semibold mt-1">
              <Icon name="mdi-gift-outline" :size="14" color="#059669" /> Plus que {{ remaining }} → 1 offert
            </div>
            <div v-else class="text-xs text-emerald-600 font-semibold mt-1">
              🎉 Récompense atteinte dès la création !
            </div>

            <div v-if="showStamps" class="grid grid-cols-5 gap-2 mt-4 max-w-[300px]">
              <span
                v-for="stamp in stamps"
                :key="stamp.n"
                class="aspect-square rounded-full flex items-center justify-center"
                :class="stamp.filled ? 'bg-secondary' : 'border-2 border-dashed border-gray'"
              >
                <Icon v-if="stamp.filled" name="mdi-check-bold" :size="14" color="#419e83" />
                <span v-else class="text-[11px] text-slate-400 tabular-nums">{{ stamp.n }}</span>
              </span>
              <span
                v-if="overflow > 0"
                class="aspect-square rounded-full flex items-center justify-center border-2 border-solid border-secondary-darkest text-secondary-darkest text-xs font-semibold tabular-nums"
              >
                +{{ overflow }}
              </span>
            </div>
            <div v-else class="mt-4 h-3 bg-gray-light rounded-full overflow-hidden">
              <div class="h-full rounded-full bg-primary" :style="{ width: `${productPercent}%` }" />
            </div>
          </template>

          <!-- Points model -->
          <template v-else>
            <div class="flex items-baseline gap-2 mt-1">
              <span class="text-3xl font-semibold text-primary tabular-nums">{{ selectedLineCount }}</span>
              <span class="text-[13px] text-gray-dark">achat{{ selectedLineCount > 1 ? 's' : '' }} sélectionné{{ selectedLineCount > 1 ? 's' : '' }}</span>
            </div>
            <div v-if="ruleTeaser" class="text-xs text-gray-dark font-semibold mt-1">
              {{ ruleTeaser }}
            </div>
            <div v-else-if="threshold > 0" class="text-xs text-gray-dark font-semibold mt-1">
              Objectif : {{ threshold }} points
            </div>
            <div class="mt-4 flex items-start gap-2 bg-primary-lightest rounded-lg p-3">
              <Icon name="mdi-information-outline" :size="16" color="#3513e2" />
              <span class="text-[11px] text-slate-600 leading-snug">
                Les points seront crédités à la création, à partir des achats sélectionnés.
              </span>
            </div>
          </template>

          <div class="flex flex-col gap-2 mt-5">
            <button
              type="button"
              class="bg-primary text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-opacity"
              :class="selectedBuyer ? 'hover:opacity-90' : 'opacity-60 cursor-not-allowed'"
              :disabled="!selectedBuyer"
              @click="step = 3"
            >
              Continuer
            </button>
            <button
              type="button"
              class="text-slate-500 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-light transition-colors"
              @click="step = 1"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </div>

      <!-- STEP 3 : finalization -->
      <div v-else class="flex justify-center">
        <div class="bg-white shadow rounded-lg p-8 max-w-[520px] w-full text-center">
          <div class="w-16 h-16 rounded-full bg-secondary-lightest flex items-center justify-center mx-auto mb-4">
            <Icon name="mdi-card-account-details-star-outline" :size="34" color="#419e83" />
          </div>
          <div class="text-lg font-bold text-primary">
            Finaliser la carte de {{ clientName }}
          </div>
          <div class="text-[13px] text-slate-500 mt-1">
            {{ ruleTeaser ?? (isPointCard ? `Objectif : ${threshold} points` : `Règle ${threshold} produits achetés = 1 offert`) }}
          </div>

          <div v-if="showStamps" class="grid grid-cols-5 gap-2 my-6 max-w-[340px] mx-auto">
            <span
              v-for="stamp in stamps"
              :key="stamp.n"
              class="aspect-square rounded-full flex items-center justify-center"
              :class="stamp.filled ? 'bg-secondary' : 'border-2 border-dashed border-gray'"
            >
              <Icon v-if="stamp.filled" name="mdi-check-bold" :size="15" color="#419e83" />
              <span v-else class="text-[11px] text-slate-400 tabular-nums">{{ stamp.n }}</span>
            </span>
            <span
              v-if="overflow > 0"
              class="aspect-square rounded-full flex items-center justify-center border-2 border-solid border-secondary-darkest text-secondary-darkest text-xs font-semibold tabular-nums"
            >
              +{{ overflow }}
            </span>
          </div>

          <div v-if="!isPointCard" class="inline-flex items-center gap-2 bg-primary-lightest rounded-lg px-5 py-3 mb-6">
            <span class="text-2xl font-semibold text-primary tabular-nums">{{ selectedCount }}/{{ threshold }}</span>
            <span class="text-xs text-gray-dark text-left">produits reportés<br>sur la nouvelle carte</span>
          </div>
          <div v-else class="inline-flex items-center gap-2 bg-primary-lightest rounded-lg px-5 py-3 mb-6">
            <span class="text-2xl font-semibold text-primary tabular-nums">{{ selectedLineCount }}</span>
            <span class="text-xs text-gray-dark text-left">achat{{ selectedLineCount > 1 ? 's' : '' }} reporté{{ selectedLineCount > 1 ? 's' : '' }}<br>en points sur la nouvelle carte</span>
          </div>

          <div class="flex gap-3 justify-center">
            <button
              type="button"
              class="text-slate-500 rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-gray-light transition-colors"
              @click="step = 2"
            >
              Retour
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 bg-primary text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
              :disabled="submitting"
              @click="createCard"
            >
              <Icon name="mdi-check-circle" :size="18" color="#fff" />Confirmer et créer la carte
            </button>
          </div>
        </div>
      </div>
    </div>
  </BaseContainer>
</template>
